import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Camera, 
  ExternalLink, 
  Settings, 
  Plus,
  Trash2,
  Edit,
  Video,
  Shield,
  MapPin
} from "lucide-react";

export default function AdminSecurityCameras() {
  const { token } = useAuth();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    portal_url: ""
  });

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    try {
      const response = await axios.get(`${API}/admin/security-cameras`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCameras(response.data);
    } catch (error) {
      // If endpoint doesn't exist yet, use empty array
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCamera = async () => {
    if (!formData.name || !formData.portal_url) {
      toast.error("Please fill in camera name and portal URL");
      return;
    }

    try {
      if (editingCamera) {
        await axios.put(`${API}/admin/security-cameras/${editingCamera.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Camera updated successfully!");
      } else {
        await axios.post(`${API}/admin/security-cameras`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Camera added successfully!");
      }
      setConfigOpen(false);
      setEditingCamera(null);
      setFormData({ name: "", location: "", portal_url: "" });
      fetchCameras();
    } catch (error) {
      toast.error("Failed to save camera configuration");
    }
  };

  const handleDeleteCamera = async (id) => {
    if (!confirm("Are you sure you want to delete this camera?")) return;
    
    try {
      await axios.delete(`${API}/admin/security-cameras/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Camera deleted");
      fetchCameras();
    } catch (error) {
      toast.error("Failed to delete camera");
    }
  };

  const openEditDialog = (camera) => {
    setEditingCamera(camera);
    setFormData({
      name: camera.name,
      location: camera.location || "",
      portal_url: camera.portal_url
    });
    setConfigOpen(true);
  };

  const openAddDialog = () => {
    setEditingCamera(null);
    setFormData({ name: "", location: "", portal_url: "" });
    setConfigOpen(true);
  };

  const openCameraPortal = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-security-cameras-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
                <Shield className="w-8 h-8" />
                Security Cameras
              </h1>
              <p className="text-text-muted mt-1">Monitor property security camera feeds</p>
            </div>
            <Button 
              onClick={openAddDialog}
              className="bg-primary hover:bg-primary-800 rounded-full h-12 px-6"
              data-testid="add-camera-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Camera
            </Button>
          </div>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Camera className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">How it works</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Add your camera cloud portal URLs below. When you click "View Live Feed", 
                    it will open your camera vendor's portal in a new tab where you can view the live stream.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cameras Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-primary">Loading cameras...</div>
            </div>
          ) : cameras.length === 0 ? (
            <Card className="border-stone-200">
              <CardContent className="py-16 text-center">
                <Video className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-600 mb-2">No Cameras Configured</h3>
                <p className="text-text-muted mb-6 max-w-md mx-auto">
                  Add your security camera cloud portal URLs to quickly access live feeds from this dashboard.
                </p>
                <Button onClick={openAddDialog} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Camera
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cameras.map((camera) => (
                <Card 
                  key={camera.id} 
                  className="border-stone-200 hover:shadow-lg transition-shadow"
                  data-testid={`camera-card-${camera.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Camera className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{camera.name}</CardTitle>
                          {camera.location && (
                            <p className="text-sm text-text-muted flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {camera.location}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <Button 
                        onClick={() => openCameraPortal(camera.portal_url)}
                        className="w-full bg-primary hover:bg-primary-800"
                        data-testid={`view-camera-btn-${camera.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Live Feed
                      </Button>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex-1"
                          onClick={() => openEditDialog(camera)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCamera(camera.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Quick Access Section */}
          {cameras.length > 0 && (
            <Card className="mt-6 border-stone-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ExternalLink className="w-5 h-5" />
                  Quick Access - All Cameras
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {cameras.map((camera) => (
                    <Button
                      key={camera.id}
                      variant="outline"
                      onClick={() => openCameraPortal(camera.portal_url)}
                      className="rounded-full"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {camera.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Add/Edit Camera Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {editingCamera ? "Edit Camera" : "Add Camera"}
            </DialogTitle>
            <DialogDescription>
              Configure your security camera cloud portal access
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Camera Name *</Label>
              <Input
                id="name"
                data-testid="camera-name-input"
                placeholder="e.g., Main Entrance, Parking Lot"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-testid="camera-location-input"
                placeholder="e.g., Alakia Property, Dada Estate"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="portal_url">Cloud Portal URL *</Label>
              <Input
                id="portal_url"
                data-testid="camera-url-input"
                placeholder="https://your-camera-cloud-portal.com"
                value={formData.portal_url}
                onChange={(e) => setFormData({...formData, portal_url: e.target.value})}
              />
              <p className="text-xs text-text-muted">
                Enter the URL of your camera vendor's cloud portal or web interface
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCamera}
              className="bg-primary hover:bg-primary-800"
              data-testid="save-camera-btn"
            >
              {editingCamera ? "Save Changes" : "Add Camera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
